import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

// Types
interface PlatformStats {
  organizations: {
    total: number;
    active: number;
    pending: number;
  };
  plans: {
    free: number;
    starter: number;
    pro: number;
    enterprise: number;
  };
  platform_admins: number;
}

interface PlatformAdmin {
  id: string;
  email: string;
  name: string | null;
  role: string;
  last_login_at: string | null;
  created_at: string;
}

interface Organization {
  id: string;
  name: string;
  slug: string;
  database_id: string | null;
  database_status: string;
  plan: string;
  max_videos: number;
  max_storage_gb: number;
  created_at: string;
}

interface PlatformSettingsData {
  settings: Record<string, { value: string; is_configured: boolean; updated_at: string }>;
  services: Record<string, { configured: boolean; keys: string[] }>;
}

interface ServiceConfig {
  name: string;
  description: string;
  keys: Array<{
    key: string;
    label: string;
    type: 'text' | 'password';
    placeholder: string;
  }>;
}

const SERVICE_CONFIGS: Record<string, ServiceConfig> = {
  stripe: {
    name: 'Stripe',
    description: 'Payment processing for subscriptions and billing',
    keys: [
      { key: 'stripe_secret_key', label: 'Secret Key', type: 'password', placeholder: 'sk_live_...' },
      { key: 'stripe_webhook_secret', label: 'Webhook Secret', type: 'password', placeholder: 'whsec_...' },
      { key: 'stripe_publishable_key', label: 'Publishable Key', type: 'text', placeholder: 'pk_live_...' },
    ],
  },
  aws_ses: {
    name: 'AWS SES',
    description: 'Email delivery for magic links and notifications',
    keys: [
      { key: 'aws_access_key_id', label: 'Access Key ID', type: 'password', placeholder: 'AKIA...' },
      { key: 'aws_secret_access_key', label: 'Secret Access Key', type: 'password', placeholder: '********' },
      { key: 'aws_region', label: 'Region', type: 'text', placeholder: 'us-east-1' },
      { key: 'from_email', label: 'From Email', type: 'text', placeholder: 'noreply@yourdomain.com' },
    ],
  },
  backblaze: {
    name: 'Backblaze B2',
    description: 'Offsite backup storage for disaster recovery',
    keys: [
      { key: 'b2_application_key_id', label: 'Application Key ID', type: 'password', placeholder: '...' },
      { key: 'b2_application_key', label: 'Application Key', type: 'password', placeholder: '********' },
      { key: 'b2_bucket_id', label: 'Bucket ID', type: 'text', placeholder: '...' },
      { key: 'b2_bucket_name', label: 'Bucket Name', type: 'text', placeholder: 'my-backups' },
    ],
  },
  assemblyai: {
    name: 'AssemblyAI',
    description: 'Transcription service for video content',
    keys: [
      { key: 'assemblyai_api_key', label: 'API Key', type: 'password', placeholder: '********' },
    ],
  },
  youtube: {
    name: 'YouTube API',
    description: 'Video metadata fetching from YouTube',
    keys: [
      { key: 'youtube_api_key', label: 'API Key', type: 'password', placeholder: 'AIza...' },
    ],
  },
  google_oauth: {
    name: 'Google OAuth',
    description: 'OAuth credentials for YouTube channel integration (allows users to import their own videos with captions)',
    keys: [
      { key: 'google_client_id', label: 'Client ID', type: 'text', placeholder: '123456789-xxx.apps.googleusercontent.com' },
      { key: 'google_client_secret', label: 'Client Secret', type: 'password', placeholder: 'GOCSPX-...' },
    ],
  },
  cloudflare: {
    name: 'Cloudflare',
    description: 'API access for tenant database provisioning',
    keys: [
      { key: 'cloudflare_api_token', label: 'API Token', type: 'password', placeholder: '********' },
      { key: 'cloudflare_account_id', label: 'Account ID', type: 'text', placeholder: '...' },
    ],
  },
};

export function PlatformAdminPage() {
  const [activeTab, setActiveTab] = useState<'overview' | 'organizations' | 'users' | 'admins' | 'settings'>('overview');

  // Check if user is platform admin
  const { data: platformMe, isLoading: checkingAccess, error: accessError } = useQuery({
    queryKey: ['platform-me'],
    queryFn: () => api.get<{ data: { id: string; email: string; role: string } }>('/platform/me'),
    retry: false,
  });

  if (checkingAccess) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (accessError || !platformMe?.data) {
    return (
      <div className="card p-8 text-center">
        <div className="text-red-600 mb-4">
          <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-600">You don't have permission to access the Platform Admin panel.</p>
        <p className="text-sm text-gray-500 mt-2">Only platform super admins can access this area.</p>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', name: 'Overview' },
    { id: 'organizations', name: 'Organizations' },
    { id: 'users', name: 'Users' },
    { id: 'admins', name: 'Platform Admins' },
    { id: 'settings', name: 'Settings' },
  ];

  return (
    <div>
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Platform Admin</h1>
            <p className="text-sm text-gray-500">Manage the SearchMyContent platform</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`pb-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'overview' && <OverviewTab />}
      {activeTab === 'organizations' && <OrganizationsTab />}
      {activeTab === 'users' && <UsersTab />}
      {activeTab === 'admins' && <AdminsTab />}
      {activeTab === 'settings' && <SettingsTab />}
    </div>
  );
}

function OverviewTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['platform-stats'],
    queryFn: () => api.get<{ data: PlatformStats }>('/platform/stats'),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  const stats = data?.data;

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card p-6">
          <p className="text-sm text-gray-500">Total Organizations</p>
          <p className="text-3xl font-bold text-gray-900">{stats?.organizations.total || 0}</p>
          <p className="text-sm text-green-600 mt-1">{stats?.organizations.active || 0} active</p>
        </div>
        <div className="card p-6">
          <p className="text-sm text-gray-500">Pending Setup</p>
          <p className="text-3xl font-bold text-gray-900">{stats?.organizations.pending || 0}</p>
          <p className="text-sm text-yellow-600 mt-1">awaiting provisioning</p>
        </div>
        <div className="card p-6">
          <p className="text-sm text-gray-500">Platform Admins</p>
          <p className="text-3xl font-bold text-gray-900">{stats?.platform_admins || 0}</p>
          <p className="text-sm text-purple-600 mt-1">super admins</p>
        </div>
        <div className="card p-6">
          <p className="text-sm text-gray-500">Paid Plans</p>
          <p className="text-3xl font-bold text-gray-900">
            {(stats?.plans.starter || 0) + (stats?.plans.pro || 0) + (stats?.plans.enterprise || 0)}
          </p>
          <p className="text-sm text-blue-600 mt-1">{stats?.plans.free || 0} free</p>
        </div>
      </div>

      {/* Plans Breakdown */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Plans Distribution</h3>
        <div className="grid gap-4 sm:grid-cols-4">
          {['free', 'starter', 'pro', 'enterprise'].map((plan) => (
            <div key={plan} className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-900">{stats?.plans[plan as keyof typeof stats.plans] || 0}</p>
              <p className="text-sm text-gray-500 capitalize">{plan}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function OrganizationsTab() {
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [editForm, setEditForm] = useState({ plan: '', database_status: '' });
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['platform-organizations'],
    queryFn: () => api.get<{ data: Organization[] }>('/platform/organizations'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: { plan?: string; database_status?: string } }) =>
      api.put(`/platform/organizations/${id}`, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-organizations'] });
      setEditingOrg(null);
    },
  });

  const handleEdit = (org: Organization) => {
    setEditingOrg(org);
    setEditForm({ plan: org.plan, database_status: org.database_status });
  };

  const handleSave = () => {
    if (!editingOrg) return;
    const updates: { plan?: string; database_status?: string } = {};
    if (editForm.plan !== editingOrg.plan) updates.plan = editForm.plan;
    if (editForm.database_status !== editingOrg.database_status) updates.database_status = editForm.database_status;
    if (Object.keys(updates).length > 0) {
      updateMutation.mutate({ id: editingOrg.id, updates });
    } else {
      setEditingOrg(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  const organizations = data?.data || [];

  return (
    <div>
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">All Organizations</h3>
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Organization</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {organizations.map((org) => (
              <tr key={org.id}>
                <td className="px-6 py-4">
                  <div>
                    <p className="font-medium text-gray-900">{org.name}</p>
                    <p className="text-sm text-gray-500">{org.slug}</p>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full capitalize ${
                    org.plan === 'enterprise' ? 'bg-purple-100 text-purple-800' :
                    org.plan === 'pro' ? 'bg-blue-100 text-blue-800' :
                    org.plan === 'starter' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {org.plan}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                    org.database_status === 'active' ? 'bg-green-100 text-green-800' :
                    org.database_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    org.database_status === 'provisioning' ? 'bg-blue-100 text-blue-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {org.database_status}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {new Date(org.created_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 text-right">
                  <button
                    onClick={() => handleEdit(org)}
                    className="text-purple-600 hover:text-purple-900 text-sm font-medium"
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
            {organizations.length === 0 && (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                  No organizations yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Edit Organization Modal */}
      {editingOrg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Organization</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
                <p className="text-gray-900">{editingOrg.name}</p>
                <p className="text-sm text-gray-500">{editingOrg.slug}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
                <select
                  value={editForm.plan}
                  onChange={(e) => setEditForm({ ...editForm, plan: e.target.value })}
                  className="input"
                >
                  <option value="free">Free</option>
                  <option value="starter">Starter</option>
                  <option value="pro">Pro</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={editForm.database_status}
                  onChange={(e) => setEditForm({ ...editForm, database_status: e.target.value })}
                  className="input"
                >
                  <option value="pending">Pending</option>
                  <option value="provisioning">Provisioning</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setEditingOrg(null)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={updateMutation.isPending}
                  className="btn-primary flex-1"
                >
                  {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface OrganizationUser {
  id: string;
  organization_id: string;
  email: string;
  name: string | null;
  role: string;
  email_verified: boolean;
  last_login_at: string | null;
  created_at: string;
  organization_name: string;
  organization_slug: string;
}

function UsersTab() {
  const [editingUser, setEditingUser] = useState<OrganizationUser | null>(null);
  const [editForm, setEditForm] = useState({ name: '', role: '' });
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['platform-users'],
    queryFn: () => api.get<{ data: OrganizationUser[] }>('/platform/users'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: { name?: string; role?: string } }) =>
      api.put(`/platform/users/${id}`, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-users'] });
      setEditingUser(null);
      setActionMessage({ type: 'success', message: 'User updated successfully' });
      setTimeout(() => setActionMessage(null), 3000);
    },
    onError: () => {
      setActionMessage({ type: 'error', message: 'Failed to update user' });
      setTimeout(() => setActionMessage(null), 3000);
    },
  });

  const sendVerificationMutation = useMutation({
    mutationFn: (userId: string) => api.post(`/platform/users/${userId}/send-verification`),
    onSuccess: (_, userId) => {
      const user = data?.data?.find(u => u.id === userId);
      setActionMessage({ type: 'success', message: `Verification email sent to ${user?.email}` });
      setTimeout(() => setActionMessage(null), 3000);
    },
    onError: () => {
      setActionMessage({ type: 'error', message: 'Failed to send verification email. Check email service configuration.' });
      setTimeout(() => setActionMessage(null), 5000);
    },
  });

  const sendPasswordResetMutation = useMutation({
    mutationFn: (userId: string) => api.post(`/platform/users/${userId}/send-password-reset`),
    onSuccess: (_, userId) => {
      const user = data?.data?.find(u => u.id === userId);
      setActionMessage({ type: 'success', message: `Password reset email sent to ${user?.email}` });
      setTimeout(() => setActionMessage(null), 3000);
    },
    onError: () => {
      setActionMessage({ type: 'error', message: 'Failed to send password reset email. Check email service configuration.' });
      setTimeout(() => setActionMessage(null), 5000);
    },
  });

  const handleEdit = (user: OrganizationUser) => {
    setEditingUser(user);
    setEditForm({ name: user.name || '', role: user.role });
  };

  const handleSave = () => {
    if (!editingUser) return;
    const updates: { name?: string; role?: string } = {};
    if (editForm.name !== (editingUser.name || '')) updates.name = editForm.name;
    if (editForm.role !== editingUser.role) updates.role = editForm.role;
    if (Object.keys(updates).length > 0) {
      updateMutation.mutate({ id: editingUser.id, updates });
    } else {
      setEditingUser(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card p-6">
        <div className="text-red-600">Failed to load users.</div>
      </div>
    );
  }

  const users = data?.data || [];

  return (
    <div>
      {/* Action Message */}
      {actionMessage && (
        <div className={`mb-4 p-4 rounded-lg ${actionMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {actionMessage.message}
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">All Organization Users</h3>
          <p className="text-sm text-gray-500 mt-1">Users across all organizations</p>
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Organization</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Login</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => (
              <tr key={user.id}>
                <td className="px-6 py-4">
                  <div>
                    <p className="font-medium text-gray-900">{user.name || user.email}</p>
                    <p className="text-sm text-gray-500">{user.email}</p>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div>
                    <p className="font-medium text-gray-900">{user.organization_name}</p>
                    <p className="text-sm text-gray-500">{user.organization_slug}</p>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full capitalize ${
                    user.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                    user.role === 'editor' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {user.role}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                    user.email_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {user.email_verified ? 'Verified' : 'Unverified'}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {user.last_login_at ? new Date(user.last_login_at).toLocaleDateString() : 'Never'}
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => handleEdit(user)}
                      className="text-purple-600 hover:text-purple-900 text-sm font-medium"
                    >
                      Edit
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={() => {
                        if (confirm(`Send verification email to ${user.email}?`)) {
                          sendVerificationMutation.mutate(user.id);
                        }
                      }}
                      disabled={sendVerificationMutation.isPending}
                      className="text-blue-600 hover:text-blue-900 text-sm font-medium disabled:opacity-50"
                    >
                      Verify
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={() => {
                        if (confirm(`Send password reset email to ${user.email}?`)) {
                          sendPasswordResetMutation.mutate(user.id);
                        }
                      }}
                      disabled={sendPasswordResetMutation.isPending}
                      className="text-orange-600 hover:text-orange-900 text-sm font-medium disabled:opacity-50"
                    >
                      Reset PW
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                  No users yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit User</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <p className="text-gray-900">{editingUser.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
                <p className="text-gray-900">{editingUser.organization_name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="input"
                  placeholder="User's name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  className="input"
                >
                  <option value="admin">Admin</option>
                  <option value="editor">Editor</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setEditingUser(null)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={updateMutation.isPending}
                  className="btn-primary flex-1"
                >
                  {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminsTab() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ email: '', name: '', role: 'super_admin' });
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['platform-admins'],
    queryFn: () => api.get<{ data: PlatformAdmin[] }>('/platform/admins'),
  });

  const addMutation = useMutation({
    mutationFn: (admin: { email: string; name?: string; role: string }) =>
      api.post('/platform/admins', admin),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-admins'] });
      setShowAddModal(false);
      setNewAdmin({ email: '', name: '', role: 'super_admin' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/platform/admins/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-admins'] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  const admins = data?.data || [];

  return (
    <div>
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Platform Administrators</h3>
          <button onClick={() => setShowAddModal(true)} className="btn-primary">
            Add Admin
          </button>
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admin</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Login</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {admins.map((admin) => (
              <tr key={admin.id}>
                <td className="px-6 py-4">
                  <div>
                    <p className="font-medium text-gray-900">{admin.name || admin.email}</p>
                    <p className="text-sm text-gray-500">{admin.email}</p>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                    admin.role === 'super_admin' ? 'bg-purple-100 text-purple-800' :
                    admin.role === 'admin' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {admin.role.replace('_', ' ')}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {admin.last_login_at ? new Date(admin.last_login_at).toLocaleDateString() : 'Never'}
                </td>
                <td className="px-6 py-4 text-right">
                  <button
                    onClick={() => {
                      if (confirm('Are you sure you want to remove this admin?')) {
                        deleteMutation.mutate(admin.id);
                      }
                    }}
                    className="text-red-600 hover:text-red-900 text-sm font-medium"
                    disabled={deleteMutation.isPending}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add Admin Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Platform Admin</h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                addMutation.mutate({
                  email: newAdmin.email,
                  name: newAdmin.name || undefined,
                  role: newAdmin.role,
                });
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  required
                  value={newAdmin.email}
                  onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                  className="input"
                  placeholder="admin@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name (optional)</label>
                <input
                  type="text"
                  value={newAdmin.name}
                  onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
                  className="input"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={newAdmin.role}
                  onChange={(e) => setNewAdmin({ ...newAdmin, role: e.target.value })}
                  className="input"
                >
                  <option value="super_admin">Super Admin</option>
                  <option value="admin">Admin</option>
                  <option value="support">Support</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addMutation.isPending}
                  className="btn-primary flex-1"
                >
                  {addMutation.isPending ? 'Adding...' : 'Add Admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function SettingsTab() {
  const [expandedService, setExpandedService] = useState<string | null>(null);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [testingService, setTestingService] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ service: string; success: boolean; message: string } | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['platform-settings'],
    queryFn: () => api.get<{ data: PlatformSettingsData }>('/platform/settings'),
  });

  const updateMutation = useMutation({
    mutationFn: (updates: Record<string, string>) => api.put('/platform/settings', updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-settings'] });
      setFormData({});
      setExpandedService(null);
    },
  });

  const testMutation = useMutation({
    mutationFn: (service: string) => api.post<{ success: boolean; message: string }>(`/platform/settings/test/${service}`),
  });

  const handleTestService = async (service: string) => {
    setTestingService(service);
    setTestResult(null);
    try {
      const result = await testMutation.mutateAsync(service);
      setTestResult({ service, success: result.success, message: result.message });
    } catch {
      setTestResult({ service, success: false, message: 'Test failed' });
    }
    setTestingService(null);
  };

  const handleSaveService = async (service: string) => {
    const config = SERVICE_CONFIGS[service];
    const updates: Record<string, string> = {};

    for (const keyConfig of config.keys) {
      const value = formData[keyConfig.key];
      if (value && value.trim()) {
        updates[keyConfig.key] = value.trim();
      }
    }

    if (Object.keys(updates).length > 0) {
      await updateMutation.mutateAsync(updates);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card p-6">
        <div className="text-red-600">Failed to load platform settings.</div>
      </div>
    );
  }

  const settings = data?.data?.settings || {};
  const services = data?.data?.services || {};

  return (
    <div className="space-y-6">
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Platform Configuration</h3>
        <p className="text-sm text-gray-500 mb-6">
          Configure external services used by the platform. Settings are encrypted at rest.
        </p>

        <div className="space-y-4">
          {Object.entries(SERVICE_CONFIGS).map(([serviceKey, config]) => {
            const serviceStatus = services[serviceKey];
            const isConfigured = serviceStatus?.configured || false;
            const isExpanded = expandedService === serviceKey;

            return (
              <div key={serviceKey} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setExpandedService(isExpanded ? null : serviceKey)}
                  className="w-full px-4 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className={`h-3 w-3 rounded-full ${isConfigured ? 'bg-green-500' : 'bg-gray-300'}`} />
                    <div className="text-left">
                      <p className="font-medium text-gray-900">{config.name}</p>
                      <p className="text-sm text-gray-500">{config.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${isConfigured ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {isConfigured ? 'Configured' : 'Not Configured'}
                    </span>
                    <svg
                      className={`h-5 w-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                    </svg>
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-gray-200 bg-gray-50">
                    <div className="pt-4 space-y-4">
                      {config.keys.map((keyConfig) => {
                        const currentSetting = settings[keyConfig.key];
                        const displayValue = currentSetting?.is_configured ? currentSetting.value : '';

                        return (
                          <div key={keyConfig.key}>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {keyConfig.label}
                              {currentSetting?.is_configured && (
                                <span className="ml-2 text-xs text-green-600">(configured)</span>
                              )}
                            </label>
                            <input
                              type={keyConfig.type}
                              placeholder={currentSetting?.is_configured ? displayValue : keyConfig.placeholder}
                              value={formData[keyConfig.key] || ''}
                              onChange={(e) => setFormData({ ...formData, [keyConfig.key]: e.target.value })}
                              className="input"
                            />
                            {currentSetting?.updated_at && (
                              <p className="text-xs text-gray-400 mt-1">
                                Last updated: {new Date(currentSetting.updated_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        );
                      })}

                      {testResult && testResult.service === serviceKey && (
                        <div className={`p-3 rounded-md text-sm ${testResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                          {testResult.message}
                        </div>
                      )}

                      <div className="flex gap-3 pt-2">
                        <button
                          onClick={() => handleSaveService(serviceKey)}
                          disabled={updateMutation.isPending}
                          className="btn-primary"
                        >
                          {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                        </button>
                        <button
                          onClick={() => handleTestService(serviceKey)}
                          disabled={testingService === serviceKey}
                          className="btn-secondary"
                        >
                          {testingService === serviceKey ? 'Testing...' : 'Test Connection'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">General Platform Settings</h3>
        <div className="space-y-4 max-w-md">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Dashboard URL</label>
            <input
              type="text"
              placeholder={settings.dashboard_url?.value || 'https://app.searchmycontent.com'}
              value={formData.dashboard_url || ''}
              onChange={(e) => setFormData({ ...formData, dashboard_url: e.target.value })}
              className="input"
            />
            <p className="text-xs text-gray-500 mt-1">Used for magic link redirects</p>
          </div>
          <button
            onClick={() => {
              if (formData.dashboard_url) {
                updateMutation.mutate({ dashboard_url: formData.dashboard_url });
              }
            }}
            disabled={updateMutation.isPending || !formData.dashboard_url}
            className="btn-primary"
          >
            Save URL
          </button>
        </div>
      </div>
    </div>
  );
}
